import React, { useContext, useEffect, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useQueryClient } from 'react-query';
import { useImmerReducer } from 'use-immer';
import clsx from 'clsx';
import { dataSelector } from '@nextiva/global-store';
import { SnackbarProvider } from '@nextiva/next-ui-components';
import { getUserList as userListGetter } from 'api/userApi';
import { getCompanyInfo as companyInfoGetter } from 'api/companyService';
import { SideNav } from 'components';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { Snackbar } from 'components/Snackbar';
import { LocationService, getSMSCampaignStatus } from 'services';
import { reducer } from 'RootContext/contextReducer';
import { useGetInfo } from 'hooks/infoService';
import { usePostPolicyDecisionGrantRequest } from 'hooks/usePostPolicyDecisionGrantRequest/usePostPolicyDecisionGrantRequest';
import { useGetAccountV2Summary } from 'hooks/useGetAccountV2Summary/useGetAccountV2Summary';
import { getPolicyDecisionRequestPayloadForBMLFlow } from 'constants/purchaseLicenseModalConstants';

import { RootContext, InitialState } from 'RootContext/rootContext';
import { setPurchaseLicenseFlowGrantStatus } from 'globalReducers/globalPolicyDecisionRequest/globalPolicyDecisionRequestActions';
import { setGlobalAccountV2Summary } from 'globalReducers/globalAccountV2Summary/globalAccountV2SummaryActions';
import { getLicenseList as licenseListGetter } from 'api/licenseService';
import {
  getManagersInfo as managersInfoGetter,
  getTeams as teamsInfoGetter,
  getUsersBasicInfo as usersBasicInfoGetter,
} from 'api/iamOrchestrationApi';
import { actionTypes } from 'containers/pages/Teams/redux/actionTypes';

// import css libraries
import '@nextiva/crm-fonts/lib/custom-icons.css';
import '@nextiva/crm-fonts/lib/fonts.css';
import '@nextiva/nextiva-location-js/dist/styles.css';
import '@nextiva/global-header__simple-header/dist/styles.css';
import '@nextiva/platform-modules__side-nav/dist/styles.css';

// @TODO: convert App.scss to an imported _m-app file
import styles from './App.scss';
import '../../styles/application/main.module.scss';
import { CAMPAIGN_STATUS_KEY } from '../pages/Messaging/CampaignDialog/constants.ts';

const actions = {};

export const CreateRootContext = ({ children }) => {
  const [rootState, rootDispatch] = useImmerReducer(reducer, InitialState);

  const contextValue = useMemo(
    () => ({
      rootState,
      rootDispatch,
      actions,
    }),
    [rootState, rootDispatch],
  );

  return (
    <RootContext.Provider value={contextValue}>{children}</RootContext.Provider>
  );
};

const App = ({
  pageMeta: { title, isHomePage },
  children,
  setPurchaseLicenseFlowGrantStatus,
  setGlobalAccountV2Summary,
}) => {
  const globalStoreData = useSelector(dataSelector);
  const dispatch = useDispatch();
  const {
    featureState: { authV3Enabled, globalNavigation },
    corpAcctNbr,
  } = useContext(FeatureContext);
  useGetInfo();
  const clientQuery = useQueryClient();

  const processPolicyDecisionGrantRequest = usePostPolicyDecisionGrantRequest();
  useGetAccountV2Summary(setGlobalAccountV2Summary);

  const getLicenseList = () =>
    clientQuery.prefetchQuery('licenseList', () =>
      licenseListGetter(authV3Enabled, corpAcctNbr),
    );

  const getTimezones = () =>
    clientQuery.prefetchQuery('timezoneOptions', LocationService.getTimezones);

  const getCompanyInfo = () =>
    clientQuery.prefetchQuery('companyInfo', () =>
      companyInfoGetter(authV3Enabled),
    );

  const getManagersInfo = () =>
    clientQuery.prefetchQuery('managersInfo', managersInfoGetter);

  const getLocations = () =>
    clientQuery.prefetchQuery('locations', LocationService.getLocations);

  const getCampaignStatus = () =>
    clientQuery.prefetchQuery([CAMPAIGN_STATUS_KEY], () =>
      getSMSCampaignStatus(corpAcctNbr),
    );
  const getUserList = () =>
    clientQuery.prefetchQuery('getUserList', () =>
      userListGetter(authV3Enabled),
    );
  const getTeams = () => clientQuery.fetchQuery('getTeams', teamsInfoGetter);
  const getUsersBasicInfo = () =>
    clientQuery.prefetchQuery('getUsersBasicInfo', usersBasicInfoGetter);

  useEffect(() => {
    Promise.all([
      getLicenseList(),
      getTimezones(),
      getCompanyInfo(authV3Enabled),
      getManagersInfo(),
      getLocations(),
      getCampaignStatus(),
      getUserList(),
      getUsersBasicInfo(),
    ]);

    (async () => {
      const teamsData = await getTeams();
      dispatch({
        type: actionTypes.SET_BASE_LIST,
        teamsList: teamsData,
      });
    })();

    processPolicyDecisionGrantRequest.mutate(
      getPolicyDecisionRequestPayloadForBMLFlow(
        globalStoreData?.user?.accountInfo?.corpAcctNbr ??
          globalStoreData?.user?.profileDTO?.corpAcctNbr,
      ),
      {
        onSuccess: (response) => {
          const hasPurchaseLicenseFlowPermissions = response?.data?.every(
            (grantStatus) => grantStatus.grant,
          );
          setPurchaseLicenseFlowGrantStatus(hasPurchaseLicenseFlowPermissions);
        },
      },
    );
  }, []);

  const AppHelment = React.memo(
    () => (
      <Helmet>
        <title>{title && `${title} — `}Platform Admin — NextOS</title>
      </Helmet>
    ),
    title,
  );

  return (
    <CreateRootContext>
      <SnackbarProvider>
        <AppHelment />
        <div
          className={clsx(
            styles.pageWrapper,
            'app-layout',
            globalNavigation && 'app-new-layout',
          )}
        >
          <div className={clsx(styles['side-nav'], 'wayfinder-container')}>
            {!isHomePage && <SideNav />}
          </div>

          <div className={clsx(styles.contentSection, 'main-container')}>
            <Snackbar />
            <div className={styles.main}>{children}</div>
          </div>
        </div>
      </SnackbarProvider>
    </CreateRootContext>
  );
};

const mapStateToProps = (state) => {
  return {
    pageMeta: state.pageMeta,
  };
};

const mapDispatchToProps = {
  setPurchaseLicenseFlowGrantStatus,
  setGlobalAccountV2Summary,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
